import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@tsgecart/ui';
import { AdminSkeleton } from './admin-skeleton.js';
import { AdminEmptyState } from './admin-empty-state.js';
import { AdminErrorState } from './admin-error-state.js';

export interface AdminDataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Present only for columns in the server's own sort allowlist -- clicking a non-sortable header does nothing (no client-side re-sort of a server-paginated page, which would only sort the current page, not the full result set). */
  sortable?: boolean;
  className?: string;
}

export interface AdminDataTableSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface AdminDataTableProps<T> {
  columns: AdminDataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyTitle: string;
  emptyDescription?: string;
  errorMessage?: string;
  sort?: AdminDataTableSort;
  onSortChange?: (field: string) => void;
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  rowActions?: (row: T) => ReactNode;
  skeletonRows?: number;
}

/**
 * The one table shell every admin list page (products/categories/brands/
 * inventory/media/warehouses/movements) builds on -- server-side
 * pagination and sorting only (Part 17 "no full-dataset client
 * filtering"): this component never re-sorts or re-filters `rows` itself,
 * it only renders what it was given and reports a sort-field click back
 * to the caller, which is expected to refetch with the new `sortField`.
 */
export function AdminDataTable<T>({
  columns,
  rows,
  getRowId,
  isLoading,
  isError,
  onRetry,
  emptyTitle,
  emptyDescription,
  errorMessage = 'This data could not be loaded.',
  sort,
  onSortChange,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  rowActions,
  skeletonRows = 5,
}: AdminDataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <AdminSkeleton lines={skeletonRows} className="h-10" />
      </div>
    );
  }
  if (isError) {
    return <AdminErrorState message={errorMessage} onRetry={onRetry} />;
  }
  if (rows.length === 0) {
    return <AdminEmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const allSelected = selectable && rows.length > 0 && rows.every((row) => selectedIds?.has(getRowId(row)));

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
            {selectable && (
              <th scope="col" className="w-10 py-2 pr-2">
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} scope="col" className={cn('py-2 pr-4 font-medium', column.className)}>
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    onClick={() => onSortChange(column.key)}
                    className="focus-visible:ring-brand inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2"
                  >
                    {column.header}
                    {sort?.field === column.key ? (
                      sort.direction === 'asc' ? (
                        <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="h-3 w-3" aria-hidden="true" />
                      )
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-300" aria-hidden="true" />
                    )}
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
            {rowActions && (
              <th scope="col" className="py-2 pl-4 text-right font-medium">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = getRowId(row);
            return (
              <tr key={id} className="border-b border-gray-50 last:border-0">
                {selectable && (
                  <td className="py-2.5 pr-2">
                    <input
                      type="checkbox"
                      aria-label="Select row"
                      checked={selectedIds?.has(id) ?? false}
                      onChange={() => onToggleSelect?.(id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={cn('py-2.5 pr-4', column.className)}>
                    {column.render(row)}
                  </td>
                ))}
                {rowActions && <td className="py-2.5 pl-4 text-right">{rowActions(row)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
